import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, ArrowUpDown, CalendarClock } from "lucide-react";
import { CandidateAvatar } from "../CandidateComponents";
import useScheduleInterview from "./useScheduleInterview";
import { intentBadge, qualifiedBadge, channelBadge, StatusChip, LinkedInLink } from "../screening/responseBadges";
import { computeResponseStats, pctOf, StatChip } from "../screening/ScreeningStats";
import ScreeningBreakdownModal from "../screening/ScreeningBreakdownModal";
import Pagination from "../../../components/shared_components/Pagination";
import useServerPagination from "../../hooks/useServerPagination";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const INTENT_TABS = [
    { key: "", label: "All" },
    { key: "interested", label: "Interested" },
    { key: "not_interested", label: "Not interested" },
    { key: "unknown", label: "Awaiting response" },
];

const ContactedCandidates = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const jobId = location.state?.job_id || null;
    const searchName = location.state?.searchName || null;
    const orgId = localStorage.getItem("current_org_id");

    const [intent, setIntent] = useState("");
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("responded_at"); // "responded_at" | "full_name"
    const [sortDir, setSortDir] = useState("desc"); // default: latest response on top
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalRow, setModalRow] = useState(null);
    const [selected, setSelected] = useState(new Set());

    // Schedule-interview flow, driven by this job role's GenieHire job. Scheduling ends the
    // outreach conversation — the candidate moves to Interview Management and off this
    // list, so refetch to backfill the page from the server.
    const { startBulkSchedule, scheduleUI, canSchedule } = useScheduleInterview(jobId, () => {
        setSelected(new Set());
        setModalRow(null);
        fetchContacted();
    });

    // Typing shouldn't fire a request per keystroke now that search runs server-side.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 500);
        return () => clearTimeout(timer);
    }, [search]);

    // The intent tab, search and sort are all query params — a page is a page of the
    // whole filtered set, not of whatever this component happens to be holding.
    const {
        currentPage,
        itemsPerPage,
        handlePageChange,
        handleItemsPerPageChange,
    } = useServerPagination({
        totalItems: total,
        resetKey: `${intent}|${debouncedSearch}|${sortBy}|${sortDir}`,
    });

    const fetchContacted = useCallback(() => {
        if (!orgId) { setLoading(false); return; }
        if (!jobId) { setRows([]); setTotal(0); setLoading(false); return; }
        setLoading(true);
        const params = {
            org_id: orgId,
            job_id: jobId,
            sort_by: sortBy,
            sort_dir: sortDir,
            page: currentPage,
            limit: itemsPerPage,
        };
        if (intent) params.intent = intent;
        if (debouncedSearch) params.search = debouncedSearch;
        axios
            .get(`${SOURCING_URL}/sourcing/contacted`, { params })
            .then(({ data }) => {
                setRows(data.candidates || []);
                setTotal(data.total ?? (data.candidates || []).length);
            })
            .catch(() => setError("Failed to load contacted candidates."))
            .finally(() => setLoading(false));
    }, [orgId, jobId, intent, debouncedSearch, sortBy, sortDir, currentPage, itemsPerPage]);

    useEffect(() => { fetchContacted(); }, [fetchContacted]);

    const handleSort = (column, defaultDir) => {
        if (sortBy === column) setSortDir(d => (d === "asc" ? "desc" : "asc"));
        else { setSortBy(column); setSortDir(defaultDir); }
    };

    // KPIs and selection are page-scoped — only the current page is in memory.
    const stats = computeResponseStats(rows, { submittedField: "responded_at" });

    const allOnPageSelected = rows.length > 0 && rows.every(c => selected.has(c.candidate_id));
    const togglePage = () => setSelected(prev => {
        const next = new Set(prev);
        rows.forEach(c => (allOnPageSelected ? next.delete(c.candidate_id) : next.add(c.candidate_id)));
        return next;
    });
    const toggleOne = (id) => setSelected(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const scheduleSelected = () => startBulkSchedule(rows.filter(c => selected.has(c.candidate_id)));

    const th = "px-4 py-3 text-left text-[13px] font-semibold text-gray-600 whitespace-nowrap";
    const td = "px-4 py-4 text-sm text-gray-600 align-middle";

    return (
        <div className="min-h-screen bg-bgSecondary dark:bg-none rounded-2xl">
            {/* Page header + KPI chips */}
            <div className="bg-white dark:bg-white/5 border-b border-gray-100 dark:border-gray-700/50 px-6 py-6 flex items-center gap-4 flex-wrap">
                <button onClick={() => navigate(-1)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <p className="font-sans font-normal text-[20px] leading-[14px] tracking-[0] dark:text-[#FFFFFF]/90 text-textPrimary py-2">Contacted {searchName ? ` · ${searchName}` : ""}</p>
                <div className="flex-1" />
                <div className="flex items-center gap-3 flex-wrap">
                    <StatChip label="Responses" value={stats.submitted} sub={`of ${stats.total} on this page`} />
                    <span className="w-px h-4 bg-gray-200" />
                    <StatChip label="Qualified" value={stats.qualified} tone="good" sub={stats.submitted ? `${pctOf(stats.qualified, stats.submitted)}%` : null} />
                    <span className="w-px h-4 bg-gray-200" />
                    <StatChip label="Not qualified" value={stats.notQualified} tone="warn" sub={stats.submitted ? `${pctOf(stats.notQualified, stats.submitted)}%` : null} />
                </div>
            </div>

            {/* Toolbar */}
            <div className="bg-white dark:bg-white/5 px-6 py-3 flex items-center justify-between gap-4 flex-wrap border-b border-gray-100 dark:border-gray-700/50">
                <div className="flex items-center gap-2 flex-wrap">
                    {INTENT_TABS.map(t => {
                        const active = intent === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setIntent(t.key)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                                    active
                                        ? "text-white"
                                        : "border border-gray-200 text-gray-600 hover:border-gray-300"
                                }`}
                                style={active ? { background: "linear-gradient(to right, #ec4899, #3b82f6)" } : undefined}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by name"
                            name="contacted-search"
                            autoComplete="off"
                            className="w-64 text-sm border border-gray-200 dark:border-gray-600 rounded-full pl-9 pr-3 py-2 outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100"
                        />
                    </div>
                </div>
            </div>

            {/* Bulk actions */}
            {canSchedule && selected.size > 0 && (
                <div className="bg-white dark:bg-white/5 px-6 py-2.5 border-b border-gray-100 dark:border-gray-700/50">
                    <div className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-white/5 border border-purple-100 dark:border-purple-500/30 rounded-xl shadow-sm">
                        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">{selected.size} selected</span>
                        <button onClick={togglePage} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline-offset-2 hover:underline">
                            {allOnPageSelected ? "Deselect all" : `Select all ${rows.length} on this page`}
                        </button>
                        <div className="flex-1" />
                        <button
                            onClick={scheduleSelected}
                            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-all"
                            style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                        >
                            Schedule Interview ({selected.size})
                        </button>
                        <button onClick={() => setSelected(new Set())} className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 px-2">✕</button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="px-2 sm:px-4">
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-7 h-7 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <p className="text-sm text-red-500 dark:text-red-400 text-center py-20">{error}</p>
                ) : total === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">No one contacted yet for this job role</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enroll candidates in an outreach or send a screening to see them here.</p>
                    </div>
                ) : (
                    <>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-200 dark:border-gray-600">
                                    {canSchedule && (
                                        <th className={`${th} w-10`}>
                                            <input
                                                type="checkbox"
                                                checked={allOnPageSelected}
                                                ref={el => {
                                                    if (el) el.indeterminate =
                                                        !allOnPageSelected && rows.some(c => selected.has(c.candidate_id));
                                                }}
                                                onChange={togglePage}
                                                className="accent-purple-600 cursor-pointer"
                                            />
                                        </th>
                                    )}
                                    <th className={`${th} w-65`}>
                                        <button onClick={() => handleSort("full_name", "asc")} className="inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-white">
                                            Candidate Name <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                        </button>
                                    </th>
                                    <th className={th}>Outreach</th>
                                    <th className={th}>Qualified</th>
                                    <th className={th}>Intent</th>
                                    <th className={th}>
                                        <button onClick={() => handleSort("responded_at", "desc")} className="inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-white">
                                            Responded <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                        </button>
                                    </th>
                                    <th className={th}>Answers</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(c => {
                                    const score = qualifiedBadge(c.is_qualified);
                                    const hasAnswers = !!c.engagement_status && (c.responses || (c.score_breakdown || []).length > 0);
                                    return (
                                        <tr key={c.candidate_id} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                            {canSchedule && (
                                                <td className={td}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(c.candidate_id)}
                                                        onChange={() => toggleOne(c.candidate_id)}
                                                        className="accent-purple-600 cursor-pointer"
                                                    />
                                                </td>
                                            )}
                                            <td className={`${td} max-w-[230px]`}>
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <CandidateAvatar candidate={c} size={36} />
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="font-semibold text-gray-900 dark:text-white truncate">{c.full_name || "Unknown"}</span>
                                                            <LinkedInLink url={c.linkedin_url} />
                                                        </div>
                                                        {c.email && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{c.email}</p>}
                                                        {c.current_title && (
                                                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                                                {c.current_title}{c.current_company ? ` · ${c.current_company}` : ""}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={td}>
                                                {c.sequence_name || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                            </td>
                                            <td className={td}>
                                                {score ? <StatusChip badge={score} /> : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                            </td>
                                            <td className={td}>
                                                <div className="flex items-center gap-1.5">
                                                    <StatusChip badge={intentBadge(c.intent)} />
                                                    <StatusChip badge={channelBadge(c.channel)} />
                                                </div>
                                            </td>
                                            <td className={`${td} tabular-nums`}>
                                                {c.responded_at ? new Date(c.responded_at).toLocaleDateString() : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                            </td>
                                            <td className={td}>
                                                {hasAnswers ? (
                                                    <button onClick={() => setModalRow(c)} className="text-xs font-medium text-purple-600 dark:text-purple-300 hover:text-purple-700">
                                                        View
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-300 dark:text-gray-600">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="py-4">
                        <Pagination
                            currentPage={currentPage}
                            totalItems={total}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            label="Candidates"
                        />
                    </div>
                    </>
                )}
            </div>

            {modalRow && (
                <ScreeningBreakdownModal
                    candidate={modalRow}
                    responses={modalRow.responses}
                    scoreBreakdown={modalRow.score_breakdown}
                    isQualified={modalRow.is_qualified}
                    intent={modalRow.intent}
                    submittedAt={modalRow.responded_at}
                    formId={modalRow.form_id}
                    formQuestions={modalRow.questions_snapshot}
                    onClose={() => setModalRow(null)}
                />
            )}

            {scheduleUI}
        </div>
    );
};

export default ContactedCandidates;
